import React, { useState, useEffect, useCallback } from 'react';
import { Offcanvas, Spinner, Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FiEye,
  FiVolume2,
  FiVolumeX,
  FiRefreshCw,
  FiEdit3,
  FiX,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiLogOut,
  FiNavigation,
  FiClock,
  FiSend,
  FiAlertTriangle,
  FiDownload,
  FiSave,
} from 'react-icons/fi';
import { auditLogsApi } from '../../services/api';
import { AuditTimeline as AuditTimelineType, AuditEvent } from '../../types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

interface AuditTimelineProps {
  interpretationId: string;
  show: boolean;
  onHide: () => void;
}

interface AuditTimelineButtonProps {
  onClick: () => void;
  className?: string;
}

const ACTION_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  VIEW: {
    icon: <FiEye />,
    label: 'Abrió interpretación',
    color: 'secondary',
  },
  TTS_START: {
    icon: <FiVolume2 />,
    label: 'Inició lectura TTS',
    color: 'info',
  },
  TTS_STOP: {
    icon: <FiVolumeX />,
    label: 'Detuvo lectura TTS',
    color: 'secondary',
  },
  REGENERATE_REQUEST: {
    icon: <FiRefreshCw />,
    label: 'Solicitó regeneración',
    color: 'warning',
  },
  EDIT_START: {
    icon: <FiEdit3 />,
    label: 'Inició edición',
    color: 'primary',
  },
  EDIT_CANCEL: {
    icon: <FiX />,
    label: 'Canceló edición',
    color: 'secondary',
  },
  APPROVE: {
    icon: <FiCheck />,
    label: 'Aprobó sin cambios',
    color: 'success',
  },
  APPROVE_WITH_EDIT: {
    icon: <FiCheckCircle />,
    label: 'Aprobó con edición',
    color: 'success',
  },
  REJECT: {
    icon: <FiXCircle />,
    label: 'Rechazó interpretación',
    color: 'danger',
  },
  STEP_CHANGE: {
    icon: <FiNavigation />,
    label: 'Cambió de paso',
    color: 'secondary',
  },
  LEAVE: {
    icon: <FiLogOut />,
    label: 'Salió de la página',
    color: 'secondary',
  },
  REPORT_SENT: {
    icon: <FiSend />,
    label: 'Reporte enviado',
    color: 'success',
  },
  REPORT_FAILED: {
    icon: <FiAlertTriangle />,
    label: 'Error al enviar reporte',
    color: 'danger',
  },
  REPORT_DOWNLOADED: {
    icon: <FiDownload />,
    label: 'Reporte descargado',
    color: 'primary',
  },
  // Written server-side when the auditor saves work in progress. The event records
  // that a save happened, by whom and when -- never the draft's clinical text.
  DRAFT_SAVED: {
    icon: <FiSave />,
    label: 'Guardó borrador',
    color: 'info',
  },
};

const formatTime = (timestamp: string): string => {
  try {
    const date = parseISO(timestamp);
    return format(date, 'HH:mm', { locale: es });
  } catch {
    return '--:--';
  }
};

const formatDate = (timestamp: string): string => {
  try {
    const date = parseISO(timestamp);
    return format(date, "d 'de' MMMM", { locale: es });
  } catch {
    return '';
  }
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) {
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const getEventDescription = (event: AuditEvent): string | null => {
  const { action, metadata } = event;

  switch (action) {
    case 'TTS_START':
      const section = metadata && 'section' in metadata ? metadata.section : '';
      return section === 'action_plan' ? 'Plan de acción' : 'Interpretación';

    case 'TTS_STOP':
      if (metadata && 'duration_seconds' in metadata) {
        const duration = formatDuration(metadata.duration_seconds as number);
        const completed = metadata.completed ? 'completo' : 'interrumpido';
        return `${duration} (${completed})`;
      }
      return null;

    case 'REGENERATE_REQUEST':
      if (metadata && 'reason' in metadata && metadata.reason) {
        return metadata.reason as string;
      }
      return null;

    case 'APPROVE_WITH_EDIT':
      if (metadata && 'modification_reason' in metadata) {
        return metadata.modification_reason as string;
      }
      return null;

    case 'REJECT':
      if (metadata && 'reason' in metadata) {
        const reasonLabels: Record<string, string> = {
          invalid_data: 'Datos inválidos',
          expired: 'Interpretación expirada',
          duplicate: 'Duplicada',
          other: 'Otro',
        };
        const reason = metadata.reason as string;
        const reasonLabel = reasonLabels[reason] || reason;
        const comment = 'comment' in metadata && metadata.comment ? String(metadata.comment) : null;
        return comment ? `${reasonLabel}: ${comment}` : reasonLabel;
      }
      return null;

    case 'STEP_CHANGE':
      if (metadata && 'from_step' in metadata && 'to_step' in metadata) {
        const from = (metadata.from_step as number) === 0 ? 'Interpretación' : 'Plan de Acción';
        const to = (metadata.to_step as number) === 0 ? 'Interpretación' : 'Plan de Acción';
        return `${from} → ${to}`;
      }
      return null;

    case 'LEAVE':
      if (metadata && 'time_on_page_seconds' in metadata) {
        return `Tiempo en página: ${formatDuration(metadata.time_on_page_seconds as number)}`;
      }
      return null;

    case 'REPORT_SENT':
      if (metadata && 'method' in metadata) {
        return metadata.method === 'auto' ? 'Automático' : 'Manual';
      }
      return null;

    case 'REPORT_FAILED':
      if (metadata && 'error' in metadata) {
        return metadata.error as string;
      }
      return null;

    case 'REPORT_DOWNLOADED':
      return null;

    default:
      return null;
  }
};

// Group events by date
const groupEventsByDate = (events: AuditEvent[]): Map<string, AuditEvent[]> => {
  const groups = new Map<string, AuditEvent[]>();

  events.forEach(event => {
    const dateKey = formatDate(event.timestamp);
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(event);
  });

  return groups;
};

/**
 * Button to open the audit timeline drawer
 */
export const AuditTimelineButton: React.FC<AuditTimelineButtonProps> = ({
  onClick,
  className = '',
}) => {
  return (
    <OverlayTrigger
      placement="bottom"
      overlay={<Tooltip>Historial de actividad</Tooltip>}
    >
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={onClick}
        className={`audit-timeline-btn ${className}`}
        style={{
          borderRadius: '50%',
          width: '38px',
          height: '38px',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FiClock size={18} />
      </Button>
    </OverlayTrigger>
  );
};

/**
 * Audit Timeline Drawer (Offcanvas)
 */
const AuditTimeline: React.FC<AuditTimelineProps> = ({
  interpretationId,
  show,
  onHide,
}) => {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState<AuditTimelineType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditLogsApi.getTimeline(interpretationId);
      setTimeline(data);
    } catch (err) {
      console.error('Error loading audit timeline:', err);
      setError('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [interpretationId]);

  useEffect(() => {
    if (show && !timeline && !loading) {
      loadTimeline();
    }
  }, [show, timeline, loading, loadTimeline]);

  // Reload when interpretationId changes
  useEffect(() => {
    setTimeline(null);
  }, [interpretationId]);

  const refreshTimeline = () => {
    setTimeline(null);
    loadTimeline();
  };

  // Reverse events to show newest first
  const events = timeline?.events ? [...timeline.events].reverse() : [];
  const groupedEvents = groupEventsByDate(events);

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: '380px' }}
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title className="d-flex align-items-center gap-2">
          <FiClock />
          Historial de actividad
          {timeline && (
            <Badge bg="secondary" pill className="ms-2">
              {timeline.events.length}
            </Badge>
          )}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" className="text-primary" />
            <p className="mt-2 text-muted mb-0">Cargando historial...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-5">
            <FiAlertTriangle className="text-danger mb-2" size={24} />
            <p className="text-danger mb-2">{error}</p>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={refreshTimeline}
            >
              Reintentar
            </Button>
          </div>
        )}

        {!loading && !error && timeline && (
          <div className="audit-timeline-content">
            {/* Summary */}
            {timeline.summary.total_time_seconds > 0 && (
              <div className="audit-summary p-3 bg-light border-bottom">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <small className="text-muted">
                    <strong>Tiempo total:</strong> {formatDuration(timeline.summary.total_time_seconds)}
                  </small>
                  {timeline.summary.regeneration_count > 0 && (
                    <Badge bg="warning" text="dark">
                      {timeline.summary.regeneration_count} regeneración{timeline.summary.regeneration_count > 1 ? 'es' : ''}
                    </Badge>
                  )}
                  {timeline.summary.tts_used && (
                    <Badge bg="info">TTS</Badge>
                  )}
                  {timeline.summary.was_edited && (
                    <Badge bg="primary">Editado</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="timeline-list">
              {events.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FiClock size={32} className="mb-2 opacity-50" />
                  <p className="mb-0">No hay actividad registrada</p>
                </div>
              ) : (
                Array.from(groupedEvents.entries()).map(([date, dateEvents]) => (
                  <div key={date}>
                    {/* Date header */}
                    <div className="date-header px-3 py-2 bg-light border-bottom sticky-top">
                      <small className="text-muted fw-semibold text-uppercase">
                        {date}
                      </small>
                    </div>

                    {/* Events for this date */}
                    {dateEvents.map((event, index) => {
                      const config = ACTION_CONFIG[event.action] || {
                        icon: <FiEye />,
                        label: event.action,
                        color: 'secondary',
                      };
                      const description = getEventDescription(event);

                      return (
                        <div
                          key={index}
                          className="timeline-event px-3 py-2 border-bottom d-flex gap-3"
                        >
                          {/* Icon */}
                          <div
                            className={`timeline-event-icon text-${config.color}`}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: `var(--bs-${config.color}-bg-subtle, #f8f9fa)`,
                              flexShrink: 0,
                            }}
                          >
                            {config.icon}
                          </div>

                          {/* Content */}
                          <div className="flex-grow-1 min-width-0">
                            <div className="d-flex justify-content-between align-items-start">
                              <strong className={`text-${config.color}`} style={{ fontSize: '0.9rem' }}>
                                {config.label}
                              </strong>
                              <small className="text-muted flex-shrink-0 ms-2">
                                {formatTime(event.timestamp)}
                              </small>
                            </div>
                            {description && (
                              <p className="mb-0 text-muted small text-truncate">
                                {description}
                              </p>
                            )}
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {event.auditor_name}
                            </small>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AuditTimeline;
